import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { BookOpen, ArrowRight, X } from 'lucide-react';

import { useLastArticle } from '@/hooks/useLastArticle';
import { pathFor } from '@/data/routes';
import NetlifyImg from '@/components/media/NetlifyImg';

// Migrated teal → bordeaux (mai 2026).
export default function ContinueReading({ className = '' }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const reduce = useReducedMotion();
  const { last } = useLastArticle();
  const [dismissed, setDismissed] = useState(() => isDismissed(last?.slug));

  if (!last || dismissed) return null;
  if ((last.scrollPercent || 0) < 5) return null;

  const articleHref = pathFor('article', last.lang, {
    category: last.category,
    slug: last.slug,
  });

  const dismiss = () => {
    markDismissed(last.slug);
    setDismissed(true);
  };

  const label = {
    fr: 'Reprendre la lecture',
    en: 'Continue reading',
    es: 'Continuar leyendo',
  }[lang] || 'Reprendre';

  const dismissLabel = {
    fr: 'Fermer',
    en: 'Dismiss',
    es: 'Cerrar',
  }[lang] || 'Fermer';

  const altPrefix = {
    fr: 'Illustration : ',
    en: 'Illustration: ',
    es: 'Ilustración: ',
  }[lang] || 'Illustration : ';

  return (
    <AnimatePresence>
      <motion.aside
        role="region"
        aria-labelledby="continue-reading-title"
        initial={reduce ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reduce ? undefined : { opacity: 0, scale: 0.97 }}
        className={`relative overflow-hidden rounded-card border border-navy-900/10 bg-cream-50 shadow-card md:flex md:items-center ${className}`}
      >
        {last.featuredImage && (
          <div className="aspect-[16/9] w-full overflow-hidden bg-navy-900/5 md:aspect-square md:w-32 md:shrink-0">
            <NetlifyImg
              src={last.featuredImage}
              alt={`${altPrefix}${last.title || last.slug}`}
              width={400}
              quality={75}
              className="h-full w-full object-cover"
            />
          </div>
        )}

        <div className="flex-1 p-4 md:p-5">
          <div className="flex items-start justify-between gap-3">
            <p
              id="continue-reading-title"
              className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-eyebrow text-bordeaux-700"
            >
              <BookOpen size={11} aria-hidden="true" />
              {label}
            </p>
            <button
              type="button"
              onClick={dismiss}
              aria-label={dismissLabel}
              className="-mr-1 -mt-1 rounded-btn p-1 text-gray-700 transition-colors hover:bg-cream-100 hover:text-navy-900"
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>

          <Link
            to={articleHref}
            className="mt-1 block font-serif text-base font-bold italic leading-tight text-navy-900 hover:text-bordeaux-700"
          >
            {last.title || last.slug}
          </Link>

          <div className="mt-3 flex items-center gap-2">
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={last.scrollPercent || 0}
              className="h-1 flex-1 overflow-hidden rounded-full bg-cream-100"
            >
              <div
                className="h-full bg-bordeaux-600 transition-all duration-500"
                style={{ width: `${Math.max(5, last.scrollPercent || 0)}%` }}
              />
            </div>
            <span className="font-mono text-xs tabular-nums text-gray-700">
              {last.scrollPercent || 0}%
            </span>
          </div>

          <Link
            to={articleHref}
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-bordeaux-700 hover:text-bordeaux-800"
          >
            {label} <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}

const DISMISS_KEY = 'ip_continue_dismissed_v1';

function isDismissed(slug) {
  if (!slug || typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(DISMISS_KEY) === slug;
  } catch {
    return false;
  }
}

function markDismissed(slug) {
  if (!slug || typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(DISMISS_KEY, slug);
  } catch {
    /* private mode — silent */
  }
}
